import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Checkbox,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import {
  DraggableDialog,
  DropDownDialog,
  ProgressDialog,
  PromptDialog
} from '../dialogs';
import { fs, getTempFilePath, ipcRenderer } from '../electron-modules';
import {
  DenseDivider,
  FontAwesomeIcon,
  IconToolBar,
  SlimScrollbar,
  ToolBarButton
} from '../toolbar';
import { Viewer } from "../viewer/js/main";
import { MarkupOperation, MarkupOperationInfo } from '../viewer/js/markup';
import { FolderWithLabel } from '../components';
import { useSelector, useDispatch } from 'react-redux';
import { AssetType, deleteAsset, getPointCloudAssets, selectAllAssets } from '../redux/assets-slice';
import { PythonExecutable } from '../executable';
import slash from 'slash';
import path from 'path';
import { selectDefaultFolder } from '../redux/folders-slice';
import { addPointCloudFiles } from '../asset-utils';
import { toast } from '../app';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    operationListTitle: {
      textAlign: "center",
      userSelect: "none"
    },
    operationParent: {
      display: "flex",
      flexDirection: "column"
    },
    operationList: {
      flex: 1,
      overflow: "hidden",
      overflowY: "auto"
    },
    checkBoxWithLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    mixedDataWarning: {
      padding: theme.spacing(1.0),
      color: theme.palette.warning.main,
      textAlign: "center"
    }
  })
);

const MarkupStep = (props) => {
  let data = props.data as MarkupOperationInfo;

  let icon = "fa-question-circle-o";
  if (data.operation === MarkupOperation.SELECT) {
    icon = "fa-object-group";
  } else if (data.operation === MarkupOperation.CLASSIFICATION) {
    icon = "fa-paint-brush";
  } else if (data.operation === MarkupOperation.VISIBILITY) {
    icon = "fa-eye-slash";
  }

  return (
    <ListItem button dense>
      <ListItemIcon>
        <FontAwesomeIcon icon={icon}/>
      </ListItemIcon>
      <ListItemText
        primary={`Operation #${props.index}: ${data.operation}`}
        secondary={data.description}
      />
    </ListItem>
  );
};

const PointMarkupExport = (props) => {
  const viewer = props.viewer as Viewer;
  const {open, setOpen, setProgress, visibleClouds} = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const [overwrite, setOverwrite] = useState(false);
  const [outputPath, setOutputPath] = useState(null);
  const defaultFolder = useSelector(selectDefaultFolder);

  const handleClose = () => {
    setOpen(false);
    setOverwrite(false);
    setOutputPath(null);
    setProgress(0.0);
  };

  const handleSubmit = () => {
    if (visibleClouds.length === 0) {
      toast.warning(`No exportable point cloud files found.`);
      return;
    }

    const jsonData = viewer.pointMarkupExportData();

    // Write COCO file to temp path
    let text = JSON.stringify(jsonData, null, 2);
    let tempMarkupPath = getTempFilePath("tempMarkup.txt");

    try {
      fs.writeFileSync(tempMarkupPath, text, 'utf8');
    } catch(err) {
      console.log(err);
      toast.error(`Point cloud markup failed`);

      return;
    }

    toast.success(`Point cloud markup submitted...`);

    const exe = new PythonExecutable();

    let response;
    let processTimeout = 1000;
    let commands = [
      "-p", "apply_point_markup",
      "--overwrite_file", overwrite,
      "--input_paths", inputPaths,
      "--input_json", slash(tempMarkupPath)
    ];

    if (outputPath) {
      commands = [
        ...commands,
        "--output_folder", outputPath
      ];
    }

    exe.run({
      command: commands,
      saveLogs: true,
      onLine: jsonData => {
        if (jsonData.progress) {
          setProgress(jsonData.progress);
        } else if (jsonData.output_paths) {
          response = jsonData.output_paths;
        }
      },
      onClose: () => {
        setTimeout(() => {
          if (response) {
            viewer.pointMarkupReset();

            // Delete all old las files
            visibleClouds.forEach(asset => {
              dispatch(deleteAsset(asset.id));
            });

            toast.success(`Importing modified files...`);

            // Import modified files
            addPointCloudFiles(dispatch, response, defaultFolder.id);

          } else {
            toast.error(`Error applying point markup`);
          }

          handleClose();
        }, processTimeout);
      }
    });
  };

  const inputPaths = visibleClouds.map(x => x.data.path);
  const inputFolders = inputPaths.map(x => path.dirname(x));

  let canSubmit = true;

  let pathOverlap = inputFolders.includes(outputPath);
  if (pathOverlap) {
    canSubmit = false;
  }

  if (!overwrite && (outputPath === null)) {
    canSubmit = false;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={"xs"}
    >
      <DialogTitle>{"Point Markup Export"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>

          {pathOverlap && (<Grid item xs={12} className={classes.mixedDataWarning}>
            <Typography variant="caption" align="center">
              Output folder must be different than LAS input folder. (Only applicable if overwrite is set to false)
            </Typography>
          </Grid>)}

          {/* Setting layer default */}
          <Grid item xs={12}>
            <div className={classes.checkBoxWithLabel}>
              <InputLabel>Overwrite Existing LAS files</InputLabel>

              <Checkbox
                checked={overwrite}
                onChange={(event) => {
                  setOverwrite(event.target.checked);
                  setOutputPath(null);
                }}
                size="small"
                color="secondary"
              />
            </div>
          </Grid>

          {/* Output folder */}
          {!overwrite && (<Grid item xs={12}>
            <FolderWithLabel
              title={"Output Folder"}
              label={`The output folder where generated LAS files will be created.
                This is not required if files are being overwritten.`}
              outputPath={outputPath}
              setOutputPath={setOutputPath}
            />
          </Grid>)}

        </Grid>

      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit} color="primary">
          Submit
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export const PointMarkup = (props) => {
  const classes = useStyles();

  const viewer = props.viewer as Viewer;
  const {setDefaultTab, setTabsEnabled, pointMarkupOperations,
    pointMarkupOpen, setPointMarkupOpen} = props;

  const [ignoreGround, setIgnoreGround] = useState(true);
  const [closePrompt, setClosePrompt] = useState(false);
  const [exportPrompt, setExportPrompt] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [classifyDialogOpen, setClassifyDialogOpen] = useState(false);

  const assets = useSelector(selectAllAssets);
  const pointClouds = getPointCloudAssets(assets);
  const LASfiles = pointClouds.filter(x => x.type === AssetType.LAS);
  const visibleClouds = LASfiles.filter(x => x.visible);

  const handleOpen = () => {
    setPointMarkupOpen(true);
  };

  const handleClose = () => {
    setPointMarkupOpen(false);
  };

  const checkClose = () => {
    if (isModified) {
      setClosePrompt(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (pointMarkupOpen) {
      setDefaultTab();
    } else {
      setClassifyDialogOpen(false);
    }

    viewer?.pointMarkupToggle(pointMarkupOpen);
    setTabsEnabled(!pointMarkupOpen);
  }, [viewer, pointMarkupOpen]);

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-point-markup");
    ipcRenderer?.on("open-point-markup", handleOpen);
  }, [viewer, pointMarkupOpen]);

  useEffect(() => {
    viewer?.setPointMarkupIgnoreGround(ignoreGround);
  }, [viewer, ignoreGround, pointMarkupOpen]);

  const selected = viewer?.pointMarkupSelected();
  const canExport = viewer?.pointMarkupCanExport();
  const isModified = pointMarkupOperations.length > 0;
  const mixedData = pointClouds.length !== LASfiles.length;

  // We want to use the reverse ordering of the operations
  let operations = [...pointMarkupOperations];
  operations.reverse();

  return (
    <React.Fragment>
      <DraggableDialog
        narrow
        minHeight
        open={pointMarkupOpen}
        title={"Point Cloud Markup"}
        onClose={checkClose}
      >

        {/* Label toolbar icons */}
        <IconToolBar margin={1}>

          {/* Undo last operation */}
          <ToolBarButton
            title={"Undo Operation"}
            icon={"fa-undo"}
            disabled={!isModified}
            onClick={() => {
              viewer.undoPointMarkup();
            }}
          />

          {/* Toggle using ground classified data */}
          <ToolBarButton
            title={"Ignore Ground Classified Points"}
            icon={ignoreGround ? "fa-check-square-o" : "fa-square-o"}
            onClick={() => {
              setIgnoreGround(!ignoreGround);
            }}
          />

          {/* Disable training areas */}
          <ToolBarButton
            title={"Delete Selection"}
            icon={"fa-eye-slash"}
            disabled={!selected}
            onClick={() => {
              viewer.hidePointMarkup();
            }}
          />

          {/* Choose classification */}
          <ToolBarButton
            title={"Change Classification"}
            icon={"fa-paint-brush"}
            disabled={!selected}
            onClick={() => {
              setClassifyDialogOpen(true);
            }}
          />

          {/* Apply changes to las file */}
          <ToolBarButton
            title={"Apply Changes"}
            icon={"fa-download"}
            disabled={!canExport}
            onClick={() => {
              setExportPrompt(true);
            }}
          />

        </IconToolBar>

        <DenseDivider variant="middle" spacing={5}/>

        {mixedData && (<Box className={classes.mixedDataWarning}>
          <Typography variant="caption" align="center">
            Point markup will only be applied to LAS files.
          </Typography>
        </Box>)}

        <DialogContent className={classes.operationParent}>
          <SlimScrollbar>
            <Box className={classes.operationListTitle}>
              <Typography gutterBottom variant="button" color="textSecondary">
                Latest Operations:
              </Typography>
            </Box>

            <List dense className={classes.operationList}>
              {operations.map((data, index) =>
                <MarkupStep
                  key={index}
                  index={operations.length - index}
                  data={data}
                />
              )}
            </List>
          </SlimScrollbar>
        </DialogContent>

      </DraggableDialog>

      {/* New class modal */}
      <DropDownDialog
        open={classifyDialogOpen}
        title="Reclassify Points"
        prompt="Select new point classification"
        values={[...Array(32).keys()]}
        onSubmit={(value) => {
          setClassifyDialogOpen(false);
          viewer.reclassifyPointMarkup(value);
        }}
        onCancel={() => {
          setClassifyDialogOpen(false);
        }}
      />

      {/* Close modal */}
      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          handleClose();
          setClosePrompt(false);
        }}
        title="Close Classification Window?"
        prompt={`Do you want to close the point cloud markup window? All modifications will be lost.`}
        button="Close"
      />

      {/* Conversion percent dialog */}
      <ProgressDialog
        open={conversionProgress > 0}
        text={"Applying Point Cloud Markup..."}
        percent={conversionProgress}
        noTransition
      />

      <PointMarkupExport
        viewer={viewer}
        open={exportPrompt}
        setOpen={setExportPrompt}
        setProgress={setConversionProgress}
        visibleClouds={visibleClouds}
      />

    </React.Fragment>
  );
};
